import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { DollarSign, TrendingUp, Users, Building2, RefreshCw, CheckCircle, Save } from 'lucide-react';

export default function ProfitSharingPage() {
  const [config, setConfig] = useState({ investor_pct: 50, trader_pct: 20, company_pct: 30, manager_pct: 5 });
  const [distributions, setDistributions] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [traderPayouts, setTraderPayouts] = useState([]);
  const [managerIncentives, setManagerIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [distributing, setDistributing] = useState(false);
  const [distDate, setDistDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchAll = async () => {
    try {
      const [cfgR, distR, sumR, payR, incR] = await Promise.all([
        api.get('/revenue/config'), api.get('/revenue/distributions', { params: { limit: 30 } }),
        api.get('/revenue/distribution-summary'), api.get('/revenue/trader-payouts'),
        api.get('/revenue/manager-incentives')
      ]);
      setConfig(cfgR.data); setDistributions(distR.data); setSummaries(sumR.data);
      setTraderPayouts(payR.data); setManagerIncentives(incR.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleSaveConfig = async () => {
    try {
      await api.put('/revenue/config', { investor_share_pct: parseFloat(config.investor_pct), trader_share_pct: parseFloat(config.trader_pct), company_share_pct: parseFloat(config.company_pct), manager_incentive_pct: parseFloat(config.manager_pct) });
      toast.success('Revenue config saved');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDistribute = async () => {
    setDistributing(true);
    try {
      const { data } = await api.post(`/revenue/distribute-daily?date=${distDate}`);
      toast.success(data.message);
      fetchAll();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setDistributing(false); }
  };

  const handleGeneratePayouts = async () => {
    try { const { data } = await api.post('/revenue/trader-payouts/generate'); toast.success(data.message); fetchAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handlePayPayout = async (id) => {
    try { await api.put(`/revenue/trader-payouts/${id}/pay`); toast.success('Payout paid'); fetchAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleCalcIncentives = async () => {
    try { const { data } = await api.post('/revenue/manager-incentives/calculate'); toast.success(data.message); fetchAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handlePayIncentive = async (id) => {
    try { await api.put(`/revenue/manager-incentives/${id}/pay`); toast.success('Incentive paid'); fetchAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const total = parseFloat(config.investor_pct || 0) + parseFloat(config.trader_pct || 0) + parseFloat(config.company_pct || 0);

  return (
    <div className="space-y-6" data-testid="profit-sharing-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Revenue & Profit Sharing</h1>
        <p className="text-sm text-brand-text-secondary mt-1">Auto profit distribution, trader payouts, manager incentives</p>
      </div>

      <Tabs defaultValue="config" className="space-y-4">
        <TabsList className="bg-white border border-brand-border rounded-sm p-1 flex-wrap gap-1">
          {[{ v: 'config', l: 'Configuration' }, { v: 'distributions', l: 'Distributions' }, { v: 'payouts', l: 'Trader Payouts' }, { v: 'incentives', l: 'Manager Incentives' }].map(t => (
            <TabsTrigger key={t.v} value={t.v} className="rounded-sm text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white px-3 py-1.5" data-testid={`tab-${t.v}`}>{t.l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="config" className="space-y-4">
          <Card className="border border-brand-border bg-white">
            <CardHeader><CardTitle className="font-heading text-base">Profit Sharing Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2"><Label>Investor Share %</Label><Input type="number" value={config.investor_pct} onChange={e => setConfig({...config, investor_pct: e.target.value})} data-testid="investor-pct" className="bg-white border-brand-border rounded-sm" /></div>
                <div className="space-y-2"><Label>Trader Share %</Label><Input type="number" value={config.trader_pct} onChange={e => setConfig({...config, trader_pct: e.target.value})} data-testid="trader-pct" className="bg-white border-brand-border rounded-sm" /></div>
                <div className="space-y-2"><Label>Company Share %</Label><Input type="number" value={config.company_pct} onChange={e => setConfig({...config, company_pct: e.target.value})} data-testid="company-pct" className="bg-white border-brand-border rounded-sm" /></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="space-y-2 w-48"><Label>Manager Incentive %</Label><Input type="number" value={config.manager_pct} onChange={e => setConfig({...config, manager_pct: e.target.value})} className="bg-white border-brand-border rounded-sm" /></div>
                <Badge className={`text-xs mt-6 ${Math.abs(total - 100) < 0.01 ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'} border-0`}>Total: {total}%</Badge>
              </div>
              <Button onClick={handleSaveConfig} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="save-config-btn"><Save size={14} className="mr-2" /> Save Configuration</Button>
            </CardContent>
          </Card>
          <Card className="border border-brand-border bg-white">
            <CardHeader><CardTitle className="font-heading text-base">Manual Distribution</CardTitle></CardHeader>
            <CardContent className="flex items-end gap-4">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={distDate} onChange={e => setDistDate(e.target.value)} className="bg-white border-brand-border rounded-sm w-44" data-testid="dist-date" /></div>
              <Button onClick={handleDistribute} disabled={distributing} className="bg-brand-secondary hover:bg-brand-secondary-hover text-white rounded-sm" data-testid="distribute-btn">
                <RefreshCw size={14} className={`mr-2 ${distributing ? 'animate-spin' : ''}`} /> Distribute Profits
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="distributions" className="space-y-4">
          {summaries.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[{ l: 'Last Distribution', v: summaries[0]?.date }, { l: 'Total Distributed', v: summaries.reduce((s, d) => s + (d.distributions_count || 0), 0) },
                { l: 'Total Company', v: '$' + summaries.reduce((s, d) => s + (d.total_company || 0), 0).toLocaleString() },
                { l: 'Total Trader', v: '$' + summaries.reduce((s, d) => s + (d.total_trader || 0), 0).toLocaleString() }
              ].map(c => (
                <Card key={c.l} className="border border-brand-border bg-white"><CardContent className="p-4 text-center">
                  <p className="text-xs uppercase tracking-wider text-brand-text-secondary">{c.l}</p><p className="font-heading text-lg text-brand-text-primary mt-1">{c.v}</p>
                </CardContent></Card>
              ))}
            </div>
          )}
          <Card className="border border-brand-border bg-white"><CardContent className="p-0"><div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="distributions-table"><thead><tr className="border-b border-brand-border bg-brand-bg/50">
              {['Date', 'Client', 'Trader', 'Profit', 'Investor', 'Trader Share', 'Company'].map(h => <th key={h} className="text-left py-3 px-3 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
            </tr></thead><tbody>
              {distributions.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-brand-text-secondary">No distributions yet</td></tr> :
               distributions.map(d => (
                <tr key={d.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                  <td className="py-2 px-3 text-brand-text-secondary text-xs">{d.date}</td>
                  <td className="py-2 px-3 text-brand-text-primary text-xs">{d.client_name}</td>
                  <td className="py-2 px-3 text-brand-text-secondary text-xs">{d.trader_name || '-'}</td>
                  <td className="py-2 px-3 font-medium text-brand-success text-xs">${Number(d.total_profit).toLocaleString()}</td>
                  <td className="py-2 px-3 text-xs">${Number(d.investor_share).toLocaleString()}</td>
                  <td className="py-2 px-3 text-xs">${Number(d.trader_share).toLocaleString()}</td>
                  <td className="py-2 px-3 text-xs">${Number(d.company_share).toLocaleString()}</td>
                </tr>))}
            </tbody></table></div></CardContent></Card>
        </TabsContent>

        <TabsContent value="payouts" className="space-y-4">
          <div className="flex gap-2"><Button onClick={handleGeneratePayouts} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm text-xs" data-testid="gen-payouts-btn">Generate Monthly Payouts</Button></div>
          <Card className="border border-brand-border bg-white"><CardContent className="p-0"><div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payouts-table"><thead><tr className="border-b border-brand-border bg-brand-bg/50">
              {['Trader', 'Period', 'Total Income', 'Status', 'Actions'].map(h => <th key={h} className="text-left py-3 px-3 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
            </tr></thead><tbody>
              {traderPayouts.length === 0 ? <tr><td colSpan={5} className="py-8 text-center text-brand-text-secondary">No payouts</td></tr> :
               traderPayouts.map(p => (
                <tr key={p.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover"><td className="py-2 px-3 text-brand-text-primary text-xs">{p.trader_name}</td><td className="py-2 px-3 text-xs">{p.period}</td>
                  <td className="py-2 px-3 font-medium text-brand-success text-xs">${Number(p.total_income).toLocaleString()}</td>
                  <td className="py-2 px-3"><Badge className={`text-xs border-0 ${p.status === 'paid' ? 'bg-brand-success/15 text-brand-success' : 'bg-amber-100 text-amber-700'}`}>{p.status}</Badge></td>
                  <td className="py-2 px-3">{p.status === 'pending' && <Button variant="outline" size="sm" onClick={() => handlePayPayout(p.id)} className="text-xs rounded-sm h-7" data-testid={`pay-payout-${p.id}`}><CheckCircle size={12} className="mr-1" /> Pay</Button>}</td>
                </tr>))}
            </tbody></table></div></CardContent></Card>
        </TabsContent>

        <TabsContent value="incentives" className="space-y-4">
          <div className="flex gap-2"><Button onClick={handleCalcIncentives} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm text-xs" data-testid="calc-incentives-btn">Calculate Monthly Incentives</Button></div>
          <Card className="border border-brand-border bg-white"><CardContent className="p-0"><div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="incentives-table"><thead><tr className="border-b border-brand-border bg-brand-bg/50">
              {['Manager', 'Branch', 'Period', 'Branch Profit', 'Incentive %', 'Amount', 'Status', 'Actions'].map(h => <th key={h} className="text-left py-3 px-3 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
            </tr></thead><tbody>
              {managerIncentives.length === 0 ? <tr><td colSpan={8} className="py-8 text-center text-brand-text-secondary">No incentives</td></tr> :
               managerIncentives.map(i => (
                <tr key={i.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover"><td className="py-2 px-3 text-brand-text-primary text-xs">{i.manager_name}</td><td className="py-2 px-3 text-xs">{i.branch_name}</td><td className="py-2 px-3 text-xs">{i.period}</td>
                  <td className="py-2 px-3 text-xs">${Number(i.branch_profit).toLocaleString()}</td><td className="py-2 px-3 text-xs">{i.incentive_pct}%</td>
                  <td className="py-2 px-3 font-medium text-brand-success text-xs">${Number(i.incentive_amount).toLocaleString()}</td>
                  <td className="py-2 px-3"><Badge className={`text-xs border-0 ${i.status === 'paid' ? 'bg-brand-success/15 text-brand-success' : 'bg-amber-100 text-amber-700'}`}>{i.status}</Badge></td>
                  <td className="py-2 px-3">{i.status === 'pending' && <Button variant="outline" size="sm" onClick={() => handlePayIncentive(i.id)} className="text-xs rounded-sm h-7"><CheckCircle size={12} className="mr-1" /> Pay</Button>}</td>
                </tr>))}
            </tbody></table></div></CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
